/**
 * 判断坐标是否在中国境内
 * @param {number} lng - 经度
 * @param {number} lat - 纬度
 * @returns {boolean} true表示在中国境外，false表示在中国境内
 */
function outOfChina(lng, lat) {
  return (lng < 72.004 || lng > 137.8347) || (lat < 0.8293 || lat > 55.8271);
}

/**
 * 转换经度
 * @param {number} x - 经度
 * @param {number} y - 纬度
 * @returns {number} 转换后的经度
 */
function transformLng(x, y) {
  let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
  ret += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
  ret += (20.0 * Math.sin(x * Math.PI) + 40.0 * Math.sin(x / 3.0 * Math.PI)) * 2.0 / 3.0;
  ret += (150.0 * Math.sin(x / 12.0 * Math.PI) + 300.0 * Math.sin(x / 30.0 * Math.PI)) * 2.0 / 3.0;
  return ret;
}

/**
 * 转换纬度
 * @param {number} x - 经度
 * @param {number} y - 纬度
 * @returns {number} 转换后的纬度
 */
function transformLat(x, y) {
  let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
  ret += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
  ret += (20.0 * Math.sin(y * Math.PI) + 40.0 * Math.sin(y / 3.0 * Math.PI)) * 2.0 / 3.0;
  ret += (160.0 * Math.sin(y / 12.0 * Math.PI) + 320 * Math.sin(y * Math.PI / 30.0)) * 2.0 / 3.0;
  return ret;
}

/**
 * WGS84坐标转GCJ02坐标
 * @param {number} wgsLng - WGS84经度
 * @param {number} wgsLat - WGS84纬度
 * @returns {Array} 包含GCJ02经度和纬度的数组，如果在中国境外则返回原坐标
 */
function wgs84ToGcj02(wgsLng, wgsLat) {
  if (outOfChina(wgsLng, wgsLat)) {
    return [wgsLng, wgsLat];
  }

  const dLat = transformLat(wgsLng - 105.0, wgsLat - 35.0);
  const dLng = transformLng(wgsLng - 105.0, wgsLat - 35.0);
  const radLat = wgsLat / 180.0 * Math.PI;
  let magic = Math.sin(radLat);
  magic = 1 - 0.00669342162296594323 * magic * magic;
  const sqrtMagic = Math.sqrt(magic);
  const dLat2 = (dLat * 180.0) / ((6378245.0 * (1 - 0.00669342162296594323)) / (magic * sqrtMagic) * Math.PI);
  const dLng2 = (dLng * 180.0) / (6378245.0 / sqrtMagic * Math.cos(radLat) * Math.PI);
  const mgLat = wgsLat + dLat2;
  const mgLng = wgsLng + dLng2;

  return [mgLng, mgLat];
}

/**
 * 批量WGS84坐标转GCJ02坐标（处理[[lng1, lat1], [lng2, lat2], ...]格式）
 * @param {Array} points - 包含WGS84坐标点的二维数组，每个元素为[lng, lat]格式的数组，经纬度为字符串
 * @returns {Array} 转换后的GCJ02坐标点二维数组，格式与输入相同
 */
export function batchWgs84ToGcj02(points) {
  return points.map(point => {
    // 将字符串转换为数字进行计算
    const lng = parseFloat(point[0]);
    const lat = parseFloat(point[1]);

    // 转换坐标
    const [gcjLng, gcjLat] = wgs84ToGcj02(lng, lat);

    // 将结果转回字符串并保留6位小数
    return [gcjLng.toFixed(6), gcjLat.toFixed(6)];
  });
}


/* -------------------------------------------------------------------------------------------------------------------- */
// 单组经纬度转换
/**
 * 判断坐标是否在中国境外
 * @param {number} longitude - 经度
 * @param {number} latitude - 纬度
 * @returns {boolean} true表示在中国境外，false表示在中国境内
 */
function isOutOfChina(longitude, latitude) {
  return (longitude < 72.004 || longitude > 137.8347) ||
    (latitude < 0.8293 || latitude > 55.8271);
}

/**
 * 计算经度偏移量
 * @param {number} x - 经度
 * @param {number} y - 纬度
 * @returns {number} 经度偏移量
 */
function calculateLngOffset(x, y) {
  let offset = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
  offset += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
  offset += (20.0 * Math.sin(x * Math.PI) + 40.0 * Math.sin(x / 3.0 * Math.PI)) * 2.0 / 3.0;
  offset += (150.0 * Math.sin(x / 12.0 * Math.PI) + 300.0 * Math.sin(x / 30.0 * Math.PI)) * 2.0 / 3.0;
  return offset;
}

/**
 * 计算纬度偏移量
 * @param {number} x - 经度
 * @param {number} y - 纬度
 * @returns {number} 纬度偏移量
 */
function calculateLatOffset(x, y) {
  let offset = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
  offset += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
  offset += (20.0 * Math.sin(y * Math.PI) + 40.0 * Math.sin(y / 3.0 * Math.PI)) * 2.0 / 3.0;
  offset += (160.0 * Math.sin(y / 12.0 * Math.PI) + 320 * Math.sin(y * Math.PI / 30.0)) * 2.0 / 3.0;
  return offset;
}

/**
 * 将单个WGS84坐标点转换为GCJ02坐标
 * @param {string} wgs84Lng - WGS84经度（字符串格式）
 * @param {string} wgs84Lat - WGS84纬度（字符串格式）
 * @returns {Array} 包含GCJ02经度和纬度的数组，格式为 ['lng', 'lat']
 */
export function convertWgs84ToGcj02(wgs84Lng, wgs84Lat) {
  // 将字符串转换为数字
  const longitude = parseFloat(wgs84Lng);
  const latitude = parseFloat(wgs84Lat);

  // 在中国境外不进行转换
  if (isOutOfChina(longitude, latitude)) {
    return [wgs84Lng, wgs84Lat];
  }

  // 计算偏移量
  const latOffset = calculateLatOffset(longitude - 105.0, latitude - 35.0);
  const lngOffset = calculateLngOffset(longitude - 105.0, latitude - 35.0);

  // 地球半径相关计算
  const radLat = latitude / 180.0 * Math.PI;
  let magic = Math.sin(radLat);
  magic = 1 - 0.00669342162296594323 * magic * magic;
  const sqrtMagic = Math.sqrt(magic);

  // 计算最终偏移量
  const latOffsetFinal = (latOffset * 180.0) / ((6378245.0 * (1 - 0.00669342162296594323)) / (magic * sqrtMagic) * Math.PI);
  const lngOffsetFinal = (lngOffset * 180.0) / (6378245.0 / sqrtMagic * Math.cos(radLat) * Math.PI);

  // 计算GCJ02坐标
  const gcj02Lat = latitude + latOffsetFinal;
  const gcj02Lng = longitude + lngOffsetFinal;

  // 转换为字符串并保留6位小数
  return [gcj02Lng.toFixed(6), gcj02Lat.toFixed(6)];
}
